// src/AppRoutes.tsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from './redux/hook';
import LoginPage from './pages/LoginPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardPage from './pages/dashboardPage';
import ProductCatalog from './components/products/ProductCatalog';
import InventoryRegisterPage from './pages/inventory/InventoryRegisterPage';
import InventoryQueryPage from './pages/inventory/InventoryQueryPage';
import InventoryTransferPage from './pages/inventory/InventoryTransferPage';
import InventoryRequestsPage from './pages/inventory/InventoryRequestsPage';
import InventoryBranchQueryPage from './pages/inventory/InventoryBranchQueryPage';
import InventoryGeneralQueryPage from './pages/inventory/InventoryGeneralQueryPage';
import SafetyStockPage from './pages/inventory/SafetyStockPage';
import PhysicalCountPage from './pages/inventory/PhysicalCountPage';
import SalesOrdersPage from './pages/SalesOrdersPage';
import SalesByPiecePage from './pages/SalesByPiecePage';
import SellerManagementPage from './pages/SellerManagementPage';
import SystemParametersPage from './pages/SystemParametersPage';
import ProductMovementHistoryPage from './pages/reports/ProductMovementHistoryPage';
import StatisticsPage from './pages/reports/StatisticsPage';
import RolesPage from './components/roles/RolesPage';
import { UsersPage } from './pages/users';
import ClientManagementPage from './pages/crm/ClientManagementPage';
import { SalesModalProvider } from './hooks/useSalesModal';
import { PermissionGate, DashboardHomeGate } from './components/auth/PermissionGate';
import {
  CRM_PERMISSIONS,
  INVENTORY_PERMISSIONS,
  ORDERS_PERMISSIONS,
  PRODUCT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  SETTINGS_PERMISSIONS,
} from './constants/permissions.constants';

// Protected Route: auth + forced password change gate
const ProtectedRoute: React.FC<{
  children: React.ReactElement;
  requiredRole?: string;
}> = ({ children }) => {
  const { isAuthenticated, mustChangePassword } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/force-change-password" replace />;
  }

  return children;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/force-change-password" element={<ForceChangePasswordPage />} />

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <SalesModalProvider>
              <DashboardLayout />
            </SalesModalProvider>
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <DashboardHomeGate>
              <DashboardPage />
            </DashboardHomeGate>
          }
        />
        <Route
          path="products"
          element={
            <PermissionGate permission={PRODUCT_PERMISSIONS.VIEW}>
              <ProductCatalog />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/register"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.REGISTER.VIEW}>
              <InventoryRegisterPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/query"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.WAREHOUSE.VIEW}>
              <InventoryQueryPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/transfer"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.TRANSFER.VIEW}>
              <InventoryTransferPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/requests"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.REQUEST.VIEW}>
              <InventoryRequestsPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/branch-query"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.BRANCH.VIEW}>
              <InventoryBranchQueryPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/general"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.GENERAL.VIEW}>
              <InventoryGeneralQueryPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/safety-stock"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.SAFETY_STOCK.VIEW}>
              <SafetyStockPage />
            </PermissionGate>
          }
        />
        <Route
          path="inventory/physical-count"
          element={
            <PermissionGate permission={INVENTORY_PERMISSIONS.PHYSICAL_COUNT.VIEW}>
              <PhysicalCountPage />
            </PermissionGate>
          }
        />
        <Route
          path="orders"
          element={
            <PermissionGate permission={ORDERS_PERMISSIONS.VIEW}>
              <SalesOrdersPage />
            </PermissionGate>
          }
        />
        <Route
          path="orders/sales-by-piece"
          element={
            <PermissionGate permission={ORDERS_PERMISSIONS.SALES_BY_PIECE_VIEW}>
              <SalesByPiecePage />
            </PermissionGate>
          }
        />
        <Route
          path="orders/sellers"
          element={
            <PermissionGate permission={ORDERS_PERMISSIONS.SELLER_MANAGEMENT_VIEW}>
              <SellerManagementPage />
            </PermissionGate>
          }
        />
        <Route
          path="crm/clients"
          element={
            <PermissionGate permission={CRM_PERMISSIONS.VIEW}>
              <ClientManagementPage />
            </PermissionGate>
          }
        />
        <Route
          path="reports/product-history"
          element={
            <PermissionGate permission={REPORTS_PERMISSIONS.PRODUCT_HISTORY}>
              <ProductMovementHistoryPage />
            </PermissionGate>
          }
        />
        <Route
          path="reports/statistics"
          element={
            <PermissionGate permission={REPORTS_PERMISSIONS.STATISTICS}>
              <StatisticsPage />
            </PermissionGate>
          }
        />
        <Route
          path="settings"
          element={
            <PermissionGate permission={SETTINGS_PERMISSIONS.PROPERTIES.VIEW}>
              <Navigate to="/dashboard/settings/properties" replace />
            </PermissionGate>
          }
        />
        <Route
          path="settings/properties"
          element={
            <PermissionGate permission={SETTINGS_PERMISSIONS.PROPERTIES.VIEW}>
              <SystemParametersPage />
            </PermissionGate>
          }
        />
        <Route
          path="settings/payment-methods"
          element={
            <PermissionGate permission={SETTINGS_PERMISSIONS.PROPERTIES.VIEW}>
              <SystemParametersPage />
            </PermissionGate>
          }
        />
        <Route
          path="settings/roles"
          element={
            <PermissionGate permission={SETTINGS_PERMISSIONS.ROLES.VIEW}>
              <RolesPage />
            </PermissionGate>
          }
        />
        <Route
          path="settings/users"
          element={
            <PermissionGate permission={SETTINGS_PERMISSIONS.USERS.VIEW}>
              <UsersPage />
            </PermissionGate>
          }
        />
      </Route>

      {/* Catch all redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AppRoutes;
